// src/auth.js
import { jwtDecode } from "jwt-decode";

const TOKEN_KEY = "token";
const USER_KEY = "user";
const DISTRIBUTION_USER_KEY = "distributionUser";

export const getToken = () => {
  const token = localStorage.getItem(TOKEN_KEY);
  return token ? token.trim() : null;
};

export const clearAuth = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("distributionToken");
  localStorage.removeItem("adminToken");
  localStorage.removeItem("authToken");
  localStorage.removeItem("user");
  localStorage.removeItem("distributionUser");
};

export const getDecodedToken = () => {
  const token = getToken();
  if (!token) return null;

  try {
    return jwtDecode(token);
  } catch (error) {
    console.error("Token decode failed:", error);
    clearAuth();
    return null;
  }
};

export const isTokenExpired = (decodedToken) => {
  if (!decodedToken?.exp) return false; // if backend does not send exp, don't force logout
  const currentTimeInSeconds = Math.floor(Date.now() / 1000);
  return decodedToken.exp <= currentTimeInSeconds;
};

export const getStoredUser = () => {
  try {
    const distributionUser = localStorage.getItem(DISTRIBUTION_USER_KEY);
    if (distributionUser) return JSON.parse(distributionUser);

    const user = localStorage.getItem(USER_KEY);
    if (user) return JSON.parse(user);

    return null;
  } catch (error) {
    console.error("Failed to parse stored user:", error);
    return null;
  }
};

export const getAuthUser = () => {
  const decoded = getDecodedToken();
  const storedUser = getStoredUser();

  if (!decoded && !storedUser) return null;

  const authUserId =
    storedUser?.id ||
    decoded?.user?.id ||
    decoded?.user?.userId ||
    decoded?.userId ||
    decoded?.id ||
    decoded?._id ||
    decoded?.sub ||
    null;

  const distributionId =
    storedUser?.distributionId ||
    decoded?.user?.distributionId ||
    decoded?.distributionId ||
    null;

  const email =
    storedUser?.email || decoded?.user?.email || decoded?.email || null;

  const mobile =
    storedUser?.mobile || decoded?.user?.mobile || decoded?.mobile || null;

  const role =
    storedUser?.role || decoded?.user?.role || decoded?.role || "distribution";

  return {
    authUserId,
    distributionId,
    email,
    mobile,
    role,
    rawDecoded: decoded,
    storedUser,
  };
};

export const getSafeUserId = () => {
  const auth = getAuthUser();
  return auth?.distributionId || auth?.authUserId || null;
};

export const requireUserId = () => {
  const userId = getSafeUserId();

  if (!userId) {
    clearAuth();
    throw new Error("Invalid authentication token. Please log in again.");
  }

  return userId;
};

export const isAuthenticated = () => {
  const token = getToken();
  if (!token) return false;

  const decoded = getDecodedToken();
  if (!decoded) return false;

  if (isTokenExpired(decoded)) {
    clearAuth();
    return false;
  }

  return true;
};

export const logout = () => {
  clearAuth();
};
